import {
  ArchiveIcon,
  EyeIcon,
  GearIcon,
  PencilSimpleIcon,
  PrinterIcon,
  TrayArrowUpIcon,
} from '@phosphor-icons/react';
import type { ReportDto } from '@act/shared/data-transfer-objects';
import { FlexContainer } from '@act/storybook';
import { useNavigate } from 'react-router-dom';
import ReportIconActionButton from './ReportIconActionButton';

const REPORT_ICON_SIZE = 20;

export interface ReportsListRowActionsProps {
  report: ReportDto;
  canEdit: boolean;
  archiveLoading: boolean;
  onArchiveToggle: (report: ReportDto) => void;
}

/**
 * Icon actions for a report row on the reports index table.
 * @param props - Report row identity, permissions, and archive handler.
 */
const ReportsListRowActions = ({
  report,
  canEdit,
  archiveLoading,
  onArchiveToggle,
}: ReportsListRowActionsProps) => {
  const navigate = useNavigate();
  const isArchived = report.status === 'archived';

  return (
    <FlexContainer
      direction="row"
      gap={0.5}
      justifyContent="flex-end"
      alignItems="center"
      height="auto"
      overflowAllVisible={true}
    >
      <ReportIconActionButton
        label="View"
        trackingName="reports-list-view"
        onClick={() => navigate(`/${report.id}/view`)}
      >
        <EyeIcon size={REPORT_ICON_SIZE} />
      </ReportIconActionButton>
      {canEdit && !isArchived ? (
        <ReportIconActionButton
          label="Edit report"
          trackingName="reports-list-builder"
          onClick={() => navigate(`/${report.id}`)}
        >
          <PencilSimpleIcon size={REPORT_ICON_SIZE} />
        </ReportIconActionButton>
      ) : null}
      <ReportIconActionButton
        label="Print / Save as PDF"
        trackingName="reports-list-print"
        disabled={isArchived}
        onClick={() => navigate(`/${report.id}/print`)}
      >
        <PrinterIcon size={REPORT_ICON_SIZE} />
      </ReportIconActionButton>
      {canEdit ? (
        <ReportIconActionButton
          label="Edit settings"
          trackingName="reports-list-edit"
          onClick={() => navigate(`/${report.id}/edit`)}
        >
          <GearIcon size={REPORT_ICON_SIZE} />
        </ReportIconActionButton>
      ) : null}
      {canEdit ? (
        <ReportIconActionButton
          label={isArchived ? 'Restore' : 'Archive'}
          trackingName="reports-list-archive-toggle"
          disabled={archiveLoading}
          onClick={() => onArchiveToggle(report)}
        >
          {isArchived ? (
            <TrayArrowUpIcon size={REPORT_ICON_SIZE} />
          ) : (
            <ArchiveIcon size={REPORT_ICON_SIZE} />
          )}
        </ReportIconActionButton>
      ) : null}
    </FlexContainer>
  );
};

export default ReportsListRowActions;
